import { useEffect, useState } from "react";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { useFeatureFlag } from "../../../../common/infrastructure/hooks/useFeatureFlag";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { FeatureFlagKey } from "../../../../common/enums";
import { INotificationPreferencesModel } from "../models/notificationPreferencesModel";

const NOTHING_ON: INotificationPreferencesModel = {
  newReservationsByEmail: false,
  newReservationsByPush: false,
};

type PreferenceField = keyof INotificationPreferencesModel;

/**
 * The "Нови резервации" card in "Моят профил → Известия": an employee turns on an email
 * and/or a device notification for new reservations on the classes and services they lead.
 * A switch flips at once and goes back if saving fails.
 */
export const useNewReservationNotifications = () => {
  const appNotificationsEnabled = useFeatureFlag(FeatureFlagKey.EnableAppNotifications, false);

  const { data, isLoading } = useApiQuery<INotificationPreferencesModel>({
    queryKey: [endpointsMetadata.notificationPreferences.get],
    endpoint: endpointsMetadata.notificationPreferences.get,
    globalIsLoading: false,
  });

  const { mutate, isLoading: isSaving } = useApiMutation();

  // What the switches show while a change is on its way; the saved values win again once
  // they reload.
  const [localPreferences, setLocalPreferences] =
    useState<INotificationPreferencesModel | null>(null);

  useEffect(() => {
    setLocalPreferences(null);
  }, [data]);

  const preferences = localPreferences ?? data ?? NOTHING_ON;

  // While app notifications are off the device switch is hidden, unless it is already on:
  // then it stays, so it can still be turned off.
  const canUsePush = appNotificationsEnabled || preferences.newReservationsByPush;

  const save = (field: PreferenceField, value: boolean) => {
    const previousValue = preferences[field];
    const next = { ...preferences, [field]: value };
    setLocalPreferences(next);

    mutate(
      {
        endpoint: endpointsMetadata.notificationPreferences.update,
        method: "put",
        payload: next,
        invalidateKeys: [[endpointsMetadata.notificationPreferences.get]],
        successMessage: "Настройките за известия са запазени.",
      },
      {
        onError: () => {
          // Only this switch goes back; a change to the other one may still be on its way.
          setLocalPreferences((current) => ({
            ...(current ?? next),
            [field]: previousValue,
          }));
        },
      },
    );
  };

  return {
    byEmail: preferences.newReservationsByEmail,
    byPush: preferences.newReservationsByPush,
    isLoading,
    isSaving,
    canUsePush,
    onEmailChange: (value: boolean) => save("newReservationsByEmail", value),
    onPushChange: (value: boolean) => save("newReservationsByPush", value),
  };
};
